import React, { useState, useEffect, useCallback } from 'react';
import { Surface, Pill } from '../design/primitives';
import Odometer from '../design/v2/Odometer';

/**
 * ProgressiveCard — bento card with 3-level progressive disclosure.
 * ─────────────────────────────────────────────────────────────────────────────
 * Pure presentational component. Consumes a LineItem[] from a breakdown
 * builder (see src/results/breakdown.js) plus a summary block with the
 * headline numbers. Controlled: depth comes from the parent so a depth
 * control above the card can drive every option's card at once.
 *
 * Props:
 *   items     — LineItem[] (depth 2+ rendering only)
 *   summary   — { title, total, monthly, savingVs? }
 *               savingVs?: { amount (signed), label }
 *   depth     — 1 | 2 | 3
 *   featured  — true ⇒ 2px ink border, 'Lowest true cost' chip and the total
 *               as odometer digits
 *   expandable — false ⇒ single non-clickable row (QC runner-ups at depth 1)
 *   onDrill   — () => void, fires on big-total click at depth 1
 *   titleAs   — heading element for the option's name (default 'h2')
 *
 * Visual rules (v2, "Tell-tale"; classes .vc-pcard* in public/design/v2/site.css):
 *   - Two font weights in body copy; no shadows. Featured = 2px ink border.
 *   - No colour on figures: the sign ("− $19,800", "saves", "more than")
 *     carries the meaning (docs/brand/DESIGN.md §3).
 *   - prefers-reduced-motion respected for the row expansion transition.
 *   - Depth-3 working strings are lazy-rendered: not in DOM at depth < 3
 *     unless the user has individually opened a row.
 * ─────────────────────────────────────────────────────────────────────────
 */

/* Mirror of App.js fmt() (line 534) — three lines, not worth exporting. */
const fmt = (n) =>
  n == null
    ? '–'
    : (n < 0 ? '-$' : '$') +
      Math.abs(Math.round(n)).toLocaleString('en-AU');

/* SSR-safe hook: track prefers-reduced-motion at runtime. */
function usePrefersReducedMotion() {
  const [reduced, setReduced] = useState(false);
  useEffect(() => {
    if (typeof window === 'undefined' || !window.matchMedia) return undefined;
    const mq = window.matchMedia('(prefers-reduced-motion: reduce)');
    const onChange = (e) => setReduced(e.matches);
    setReduced(mq.matches);
    /* Older Safari fires via addListener; modern browsers via
       addEventListener. Both kept for compatibility. */
    if (mq.addEventListener) {
      mq.addEventListener('change', onChange);
      return () => mq.removeEventListener('change', onChange);
    }
    mq.addListener(onChange);
    return () => mq.removeListener(onChange);
  }, []);
  return reduced;
}

/* Encapsulated expanded-set state for row workings. */
function useExpandedRows() {
  const [open, setOpen] = useState(() => new Set());
  const toggle = useCallback((key) => {
    setOpen((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  }, []);
  const isOpen = useCallback((key) => open.has(key), [open]);
  return { toggle, isOpen };
}

/* Format an item's signed display value, e.g. "− $19,800". */
function valueWithSign(item) {
  return `${item.sign === '-' ? '− ' : ''}${fmt(item.value)}`;
}

/* Build the saving/delta caption from savingVs. Returns null if absent. */
function savingCaption(savingVs) {
  if (!savingVs || !savingVs.amount) return null;
  if (savingVs.amount > 0) {
    return { text: `saves ${fmt(savingVs.amount)} vs ${savingVs.label}` };
  }
  return { text: `+${fmt(Math.abs(savingVs.amount))} more than ${savingVs.label}` };
}

/* ─── Sub-renderers ──────────────────────────────────────────────────────── */

function HeaderBlock({ summary, featured, depth, onDrill, titleAs: Title = 'h2' }) {
  const saving = savingCaption(summary.savingVs);
  const drillable = depth === 1;
  const total = fmt(summary.total);
  /* The hero's total rolls in as odometer digits; other cards show the figure. */
  const figure = featured
    ? <Odometer text={total} />
    : <span className="vc-figure" style={{ fontSize: 32, lineHeight: 1.1 }}>{total}</span>;
  return (
    <div className="vc-result__head">
      <div className="vc-result__headline">
        <Title className="vc-pcard__title">{summary.title}</Title>
        <div className="vc-label">True total cost</div>
        {drillable ? (
          <button
            type="button"
            className="vc-pcard__total"
            onClick={onDrill}
            aria-label={`Expand breakdown for ${summary.title}, true total ${total}`}
          >
            {figure}
          </button>
        ) : (
          <div>{figure}</div>
        )}
        <p className="vc-pcard__monthly">{fmt(summary.monthly)} / mo effective</p>
        {saving && <p className="vc-result__note">{saving.text}</p>}
        {drillable && (
          <p className="vc-pcard__hint">Tap the total to see what&rsquo;s behind it →</p>
        )}
      </div>
      {featured && <Pill tone="info">Lowest true cost</Pill>}
    </div>
  );
}

function ItemRow({ item, expanded, onToggle, reducedMotion }) {
  const isTotal = item.key === 'total';
  const workingId = `pcard-working-${item.key}`;
  const transition = reducedMotion
    ? 'none'
    : 'opacity 180ms ease';

  if (isTotal) {
    return (
      <div className="vc-pcard__row vc-pcard__row--total">
        <span>{item.label}</span>
        <span className="vc-pcard__value">{fmt(item.value)}</span>
      </div>
    );
  }

  return (
    <div>
      <button
        type="button"
        className="vc-pcard__row"
        onClick={onToggle}
        aria-expanded={expanded}
        aria-controls={workingId}
      >
        <span className="vc-pcard__label">{item.label}</span>
        <span className="vc-pcard__value">{valueWithSign(item)}</span>
      </button>
      {expanded && (
        <p
          id={workingId}
          className="vc-formula vc-pcard__working"
          style={{ opacity: 1, transition }}
        >
          {item.working}
        </p>
      )}
    </div>
  );
}

function SingleRow({ summary }) {
  const saving = savingCaption(summary.savingVs);
  return (
    <>
      <div className="vc-pcard__name">{summary.title}</div>
      <div className="vc-pcard__end">
        <span className="vc-figure">{fmt(summary.total)}</span>
        {saving && <span className="vc-small">{saving.text}</span>}
      </div>
    </>
  );
}

/* ─── Main component ────────────────────────────────────────────────────── */

export default function ProgressiveCard({
  items = [],
  summary,
  depth,
  featured = false,
  expandable = true,
  onDrill,
  titleAs = 'h2',
}) {
  const reducedMotion = usePrefersReducedMotion();
  const { toggle, isOpen } = useExpandedRows();

  /* Single-row mode (QC runner-ups at depth 1). Non-clickable;
     interactivity is the parent's responsibility. */
  if (!expandable) {
    return (
      <Surface tone={featured ? 'emphasis' : 'default'} className="vc-pcard vc-pcard--line">
        <SingleRow summary={summary} />
      </Surface>
    );
  }

  const showItems = depth >= 2;
  /* Lazy depth-3 gate: at depth < 3, workings are only in DOM for rows
     the user has individually opened. At depth 3, every working is in
     DOM regardless of `open` set (rows can still be toggled closed). */
  const workingExpanded = (key) => (depth >= 3 ? true : isOpen(key));

  return (
    <Surface tone={featured ? 'emphasis' : 'default'} className="vc-pcard">
      <HeaderBlock
        summary={summary}
        featured={featured}
        depth={depth}
        onDrill={onDrill}
        titleAs={titleAs}
      />
      {showItems && (
        <div className="vc-pcard__rows">
          {items.map((item) => (
            <ItemRow
              key={item.key}
              item={item}
              expanded={workingExpanded(item.key)}
              onToggle={() => toggle(item.key)}
              reducedMotion={reducedMotion}
            />
          ))}
        </div>
      )}
    </Surface>
  );
}
